import { CheckIcon, ClockIcon } from 'assets/icons';
import SeeMoreButton from 'components/seeMore/SeeMoreButton';
import Label from 'components/crm/ui/Label';
import SummaryCell from 'components/crm/ui/SummaryCell';
import { doneText, dueText, isLate } from 'helper/taskDates';
import { linkKey, taskLinks } from 'helper/taskLinks';
import { durationLabel } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { CircleDashed } from 'lucide-react';
import { useEffect, useState } from 'react';
import TaskContactCard from './TaskContactCard';
import TaskDescriptionField from './TaskDescriptionField';

// The linked records shown before « Voir plus ».
const SHOWN_LINKS = 3;

/**
 * The « Détail » tab of a task: the summary of the appointment's panel, its
 * due day and hour (red when late) beside its state (done, and when, or still
 * to do); every record it is linked to, its
 * description. A task has a single kind: no type badge, no e-mail to send. `openerOf(link)`: what opens a
 * linked record, or null. The records sit in one frame, a line between each,
 * the first three only until « Voir plus », the pill of the mission
 * description in jobbiz_app_v2, under the frame.
 */
const TaskDetailTab = ({ task, openerOf, onSave, canEdit = true }) => {
  const { text } = useText('tasks');
  const now = new Date();
  const late = isLate(task, now);
  const links = taskLinks(task);
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [task.id]);
  const when = task.dueTime
    ? `${dueText(task, now)}, ${task.dueTime.slice(0, 5)} · ${durationLabel(task.durationMin ?? 15)}`
    : dueText(task, now);
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 rounded-xl border border-gray-200">
        <SummaryCell
          icon={<ClockIcon wh={16} color="currentColor" />}
          iconClass={late ? 'text-red-600' : undefined}
          label={text('dateTimeLabel')}
          value={when}
          valueClass={late ? 'text-red-600' : undefined}
          className="border-r border-gray-200"
        />
        {task.doneAt ? (
          <SummaryCell
            icon={<CheckIcon w={14} h={11} color="currentColor" />}
            iconClass="text-green-700"
            label="État"
            value={doneText(task, now)}
            valueClass="text-green-700"
          />
        ) : (
          <SummaryCell icon={<CircleDashed size={16} aria-hidden="true" />} label="État" value="À faire" />
        )}
      </div>
      {links.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>{text('columnLinked')}</Label>
          <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
            {links.slice(0, more ? links.length : SHOWN_LINKS).map((link) => (
              <TaskContactCard key={linkKey(link)} link={link} task={task} onOpen={openerOf(link)} />
            ))}
          </div>
          {links.length > SHOWN_LINKS && (
            <SeeMoreButton open={more} onClick={() => setMore((current) => !current)}>
              {more ? text('seeLessLinks') : text('seeMoreLinks', { count: links.length - SHOWN_LINKS })}
            </SeeMoreButton>
          )}
        </div>
      )}
      <TaskDescriptionField
        key={task.id}
        initial={task.note ?? ''}
        onSave={(note) => onSave({ note })}
        canEdit={canEdit}
      />
    </div>
  );
};

export default TaskDetailTab;
